import {
  BasicInfoForTssCampaign,
  extCampaignKind,
  ExtCampaignKind,
  N_ArtworkSpec,
  N_NamedAnnotated,
  N_Placement,
  N_Specifications,
} from "@specsheet-common/shared-types";
import { getDurations } from "./specifications/duration";
import { getFileFormats } from "./specifications/fileFormat";

export const ATTR_SLOT_LENGTH = "Slot length";
export const ATTR_SPOT_DURATION = "Spot Duration";
/** JCD / JCDNZ: stored on `placement.attributes` (import from sitecard CSV). */
export const ATTR_CREATIVE_RESTRICTIONS = "Creative Restrictions";

/**
 * Match Available Duration column formatting (`getDurations` / `getDuration`):
 * compact units (e.g. `25s`), not prose like `25 seconds`.
 */
export const formatSlotLengthLikeAvailableDuration = (raw: string): string => {
  let t = raw.trim().replace(/[\u00A0\u2000-\u200B\u202F\uFEFF]/g, " ");
  if (!t) {
    return t;
  }
  t = t.replace(/\b(\d+)\s*milliseconds?\b/gi, "$1ms");
  t = t.replace(/\b(\d+)\s*minutes?\b/gi, "$1m");
  t = t.replace(/\b(\d+)\s*hours?\b/gi, "$1h");
  t = t.replace(/\b(\d+)\s*seconds?\b/gi, "$1s");
  return t.replace(/\s+/g, " ").trim();
};

const withDurationFormatting = (
  value: string | null | undefined
): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  const out = formatSlotLengthLikeAvailableDuration(value);
  return out === "" ? null : out;
};

export type StandardColumnPlacementOptions = {
  externalCampaignInfo?: BasicInfoForTssCampaign | null;
};

/** QMS, OOH, JCD(+NZ): show Slot Length column; other imports / manual bookings → blank. */
const SLOT_LENGTH_IMPORT_KINDS = new Set<string>([
  extCampaignKind.QMS,
  extCampaignKind.OOH,
  extCampaignKind.JCD,
  extCampaignKind.JCDNZ,
]);

export const parseSlotLengthImportKind = (
  externalCampaignInfo: BasicInfoForTssCampaign | null | undefined
): ExtCampaignKind | null => {
  if (!externalCampaignInfo?.extCampaignRef) {
    return null;
  }
  const prefix = externalCampaignInfo.extCampaignRef.split("-")[0];
  return SLOT_LENGTH_IMPORT_KINDS.has(prefix)
    ? (prefix as ExtCampaignKind)
    : null;
};

const getAttributeValue = (
  attrs: Array<{ name: string; value: string }> | undefined,
  name: string
): string | null => attrs?.find((a) => a.name === name)?.value ?? null;

const getSpecAttributeValue = (
  spec: N_ArtworkSpec,
  name: string
): string | null => {
  if (spec.kind !== "digitalStatic" && spec.kind !== "video") {
    return null;
  }
  return spec.value.attributes?.find((a) => a.name === name)?.value ?? null;
};

const walkSpecForAttributeName = (
  specifications: N_Specifications | undefined,
  name: string
): string | null => {
  if (!specifications?.value?.length) {
    return null;
  }
  for (const named of specifications.value) {
    const v = getSpecAttributeValue(named.value, name);
    if (v) {
      return v;
    }
  }
  return null;
};

/** QMS: "Slot length" only (placement + spec). */
const getQmsSlotLength = (
  placement: Omit<N_Placement, "attributes"> & {
    attributes?: Array<{ name: string; value: string }>;
  }
): string | null =>
  getAttributeValue(placement.attributes, ATTR_SLOT_LENGTH) ??
  walkSpecForAttributeName(placement.specifications, ATTR_SLOT_LENGTH);

/** OOH: "Spot Duration", then "Slot length" on placement + spec. */
const getOohSlotLength = (
  placement: Omit<N_Placement, "attributes"> & {
    attributes?: Array<{ name: string; value: string }>;
  }
): string | null =>
  getAttributeValue(placement.attributes, ATTR_SPOT_DURATION) ??
  getAttributeValue(placement.attributes, ATTR_SLOT_LENGTH) ??
  walkSpecForAttributeName(placement.specifications, ATTR_SPOT_DURATION) ??
  walkSpecForAttributeName(placement.specifications, ATTR_SLOT_LENGTH);

/**
 * JCD (+NZ): structured video/animated duration only (`duration (s)` → `video.durations`).
 */
export const getSlotLengthFromStructuredDurations = (
  specifications: N_Specifications | undefined
): string | null => {
  if (!specifications?.value?.length) {
    return null;
  }
  const durations = getDurations(specifications.value);
  const first = durations[0]?.value?.trim();
  return first || null;
};

/** JCD / JCDNZ: when structured duration is absent but sitecard has creative text, show this in Slot Length. */
export const JCD_SLOT_LENGTH_VARIES_BY_LOCATION = "Varies By Location";

/**
 * Use in the Slot Length column UI: only this label should deep-link to Creative Restrictions
 * (not structured durations such as `10s`).
 */
export const isVariesByLocationSlotLengthDisplay = (
  cellDisplayValue: string
): boolean =>
  formatSlotLengthLikeAvailableDuration(cellDisplayValue).toLowerCase() ===
  formatSlotLengthLikeAvailableDuration(
    JCD_SLOT_LENGTH_VARIES_BY_LOCATION
  ).toLowerCase();

const getJcdJcdnzSlotLengthDisplayValue = (
  placement: Omit<N_Placement, "attributes"> & {
    attributes?: Array<{ name: string; value: string }>;
  }
): string | null => {
  const fromSpec = withDurationFormatting(
    getSlotLengthFromStructuredDurations(placement.specifications)
  );
  if (fromSpec) {
    return fromSpec;
  }
  const creativeRestrictions = getAttributeValue(
    placement.attributes,
    ATTR_CREATIVE_RESTRICTIONS
  );
  if (creativeRestrictions?.trim()) {
    return JCD_SLOT_LENGTH_VARIES_BY_LOCATION;
  }
  return null;
};

/**
 * Column stays "Slot Length"; values are import-specific.
 * Requires {@link BasicInfoForTssCampaign} from the booking; otherwise blank.
 */
export const getSlotLengthDisplayValue = (
  placement: Omit<N_Placement, "attributes"> & {
    attributes?: Array<{ name: string; value: string }>;
  },
  externalCampaignInfo: BasicInfoForTssCampaign | null | undefined
): string | null => {
  const kind = parseSlotLengthImportKind(externalCampaignInfo ?? null);
  if (!kind) {
    return null;
  }

  if (kind === extCampaignKind.QMS) {
    return withDurationFormatting(getQmsSlotLength(placement));
  }

  if (kind === extCampaignKind.OOH) {
    return withDurationFormatting(getOohSlotLength(placement));
  }

  if (kind === extCampaignKind.JCD || kind === extCampaignKind.JCDNZ) {
    return getJcdJcdnzSlotLengthDisplayValue(placement);
  }

  return null;
};

const normalizeDurationDisplayToken = (s: string): string =>
  s.replace(/\s+/g, " ").trim();

type PlacementWithOptionalAttrs = Omit<N_Placement, "attributes"> & {
  attributes?: Array<{ name: string; value: string }>;
};

/**
 * Available Duration from structured specs only (`getDurations` — video / animated / html5 / audio).
 * Used for **JCD**, **JCDNZ**, manual bookings, other imports, and whenever we are not doing the
 * OOH/QMS+MP4 Slot Length mirror. When `hasSpotDurationAttribute` (OOH duplicate guard), returns [].
 */
const getDefaultAvailableDurationEntries = (
  placement: PlacementWithOptionalAttrs,
  hasSpotDurationAttribute: boolean
): N_NamedAnnotated<string>[] => {
  if (hasSpotDurationAttribute) {
    return [];
  }
  return getDurations(placement.specifications?.value ?? []);
};

/**
 * True when specs include MP4 as an accepted video format (OOH / QMS digital MP4 panels).
 */
export const placementHasMp4VideoFormat = (
  placement: Omit<N_Placement, "attributes"> & {
    attributes?: Array<{ name: string; value: string }>;
  }
): boolean => {
  const specs = placement.specifications?.value ?? [];
  return getFileFormats(specs).some((f) => f.value === "MP4");
};

/**
 * OOH / QMS + MP4: show the same compact duration in **Duration** as **Slot Length** when
 * structured spec durations do not already include an equivalent value (e.g. Spot Duration
 * previously hid Available Duration via `hasSpotDurationAttribute`).
 *
 * **All other cases** (including **JCD** / **JCDNZ** and non-MP4 OOH/QMS) use
 * {@link getDefaultAvailableDurationEntries} — same mapping as before this helper existed.
 */
export const getAvailableDurationEntriesOohQmsMp4Aware = (
  placement: PlacementWithOptionalAttrs,
  externalCampaignInfo: BasicInfoForTssCampaign | null | undefined,
  hasSpotDurationAttribute: boolean
): N_NamedAnnotated<string>[] => {
  const kind = parseSlotLengthImportKind(externalCampaignInfo ?? null);
  const isOohOrQms =
    kind === extCampaignKind.OOH || kind === extCampaignKind.QMS;
  const specs = placement.specifications?.value ?? [];
  /** Full structured durations from specs (used for dedupe inside OOH/QMS+MP4). */
  const structuredFromSpecs = getDurations(specs);

  if (!isOohOrQms || !placementHasMp4VideoFormat(placement)) {
    return getDefaultAvailableDurationEntries(
      placement,
      hasSpotDurationAttribute
    );
  }

  const slot = getSlotLengthDisplayValue(placement, externalCampaignInfo);
  const slotNorm = slot
    ? normalizeDurationDisplayToken(formatSlotLengthLikeAvailableDuration(slot))
    : "";

  if (!slotNorm) {
    return getDefaultAvailableDurationEntries(
      placement,
      hasSpotDurationAttribute
    );
  }

  const structuredNorms = new Set(
    structuredFromSpecs.map((d) => normalizeDurationDisplayToken(d.value))
  );
  if (structuredNorms.has(slotNorm)) {
    return structuredFromSpecs;
  }

  const synthetic: N_NamedAnnotated<string> = {
    name: null,
    value: slotNorm,
    metadata: null,
  };

  if (structuredFromSpecs.length === 0) {
    return [synthetic];
  }

  return [synthetic, ...structuredFromSpecs];
};
